
import Link from '@/components/compat/Link';
import { BarChart3, Eye, Flame, Trophy } from 'lucide-react';
import { painelSurfaceClass } from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';
import { useAnalyticsOverview } from '@/hooks/use-analytics-overview';
import { useAnalyticsGuides } from '@/hooks/use-analytics-guides';

function formatInt(n: number) {
  return new Intl.NumberFormat('pt-BR').format(n);
}

export function PainelAnalyticsPreview() {
  const { data: overview } = useAnalyticsOverview();
  const { data: guides } = useAnalyticsGuides();

  const weekSeries = overview.visitsSeries.map((d) => d.visits);
  const max = Math.max(...weekSeries, 1);
  const viewsWeek = weekSeries.reduce((a, b) => a + b, 0) || overview.pageviews;
  const topGuide = guides.topGuides[0];
  const topChannel = [...overview.channels].sort((a, b) => b.value - a.value)[0];

  return (
    <Link
      href="/painel/analytics"
      className={cn(
        'flex h-full flex-col overflow-hidden outline-none transition-[transform,box-shadow] duration-300',
        'focus-visible:ring-2 focus-visible:ring-sky-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#030023]',
        'hover:-translate-y-0.5 hover:shadow-[0_0_48px_-12px_rgba(56,189,248,0.22)]'
      )}
    >
      <section
        className={cn(
          'flex h-full flex-1 flex-col overflow-hidden',
          painelSurfaceClass,
          'bg-gradient-to-b from-blue-950/55 to-[#04032a]/90',
          'shadow-[0_0_44px_-14px_rgba(56,189,248,0.16)]'
        )}
      >
      <div className="border-b border-white/10 px-5 py-4">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-500/20 ring-1 ring-indigo-400/30">
            <BarChart3 className="h-4 w-4 text-indigo-200" />
          </div>
          <div>
            <h2 className="font-poppins text-sm font-bold text-white">Analytics (prévia)</h2>
            <p className="font-inter text-[11px] text-slate-400">Últimos 7 dias</p>
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-5 p-5">
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-inter text-[11px] font-medium uppercase tracking-wider text-indigo-200/60">
                Visualizações esta semana
              </p>
              <p className="mt-1 font-poppins text-2xl font-bold tabular-nums text-white">
                {formatInt(viewsWeek)}
              </p>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 font-poppins text-[11px] font-semibold text-emerald-200/90 ring-1 ring-emerald-400/20">
              <Eye className="h-3 w-3" />
              {overview.visitsWeek > 0 ? `${formatInt(overview.visitsWeek)} visitantes` : '—'}
            </span>
          </div>
          <div className="mt-4 flex h-14 items-end gap-1.5" aria-hidden>
            {(weekSeries.length ? weekSeries : [0, 0, 0, 0, 0, 0, 0]).map((v, i) => (
              <div
                key={i}
                className="flex-1 rounded-t-md bg-gradient-to-t from-sky-600/40 to-sky-400/80 transition-all hover:from-sky-500/50 hover:to-sky-300/90"
                style={{ height: `${Math.max(12, (v / max) * 100)}%` }}
              />
            ))}
          </div>
          <p className="mt-2 font-inter text-[10px] text-slate-400/70">Últimos 7 dias</p>
        </div>

        <div className="grid gap-3">
          <div className="flex items-center gap-3 rounded-xl border border-cyan-400/20 bg-cyan-950/20 px-3 py-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-500/20">
              <Trophy className="h-3.5 w-3.5 text-cyan-200" />
            </div>
            <div className="min-w-0">
              <p className="font-inter text-[10px] uppercase tracking-wider text-cyan-200/50">Canal top</p>
              <p className="truncate font-poppins text-sm font-semibold text-white">
                {topChannel?.name ?? '—'}
                {topChannel ? ` · ${topChannel.value}%` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-amber-400/20 bg-amber-950/15 px-3 py-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/20">
              <Flame className="h-3.5 w-3.5 text-amber-200" />
            </div>
            <div className="min-w-0">
              <p className="font-inter text-[10px] uppercase tracking-wider text-amber-200/50">Guia top</p>
              <p className="truncate font-poppins text-sm font-semibold text-white">
                {topGuide?.title ?? '—'}
                {topGuide ? ` · ${formatInt(topGuide.views)}` : ''}
              </p>
            </div>
          </div>
        </div>
      </div>
      </section>
    </Link>
  );
}
